import api from '@/libs/axiosInstance';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import type {
  ApiResponse,
  BriefTemplateListStatusFilter,
  BriefTemplateResponse,
  ListBriefTemplatesQuery,
  PaginationMeta,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { BRIEF_TEMPLATES_PATH, briefTemplateKeys } from './brief-template-keys';

type UseBriefTemplatesQueryOptions = Pick<
  ListBriefTemplatesQuery,
  'search' | 'status' | 'collabType'
> & {
  /** A caller that may not reach the briefs at all (no applications option) skips the read. */
  enabled?: boolean;
};

/** Paginated payload inside the standard envelope (contract v1.1). */
interface BriefTemplateListPayload {
  data: BriefTemplateResponse[];
  meta: PaginationMeta;
}

/**
 * The library has no pagination UI, so we ask for the backend maximum in one
 * page (contract v1.1: perPage max = 100).
 */
const LIBRARY_PER_PAGE = 100;

const buildListPath = (
  search: string,
  status: BriefTemplateListStatusFilter,
  collabType: ListBriefTemplatesQuery['collabType']
): string => {
  const params = new URLSearchParams({ status, perPage: String(LIBRARY_PER_PAGE) });
  if (search) {
    params.set('search', search);
  }
  if (collabType) {
    params.set('collabType', collabType);
  }
  return `${BRIEF_TEMPLATES_PATH}?${params.toString()}`;
};

/**
 * List the brand's collab brief templates for the library view. Search, the
 * active/archived status and the type are all applied server-side. The envelope's `data`
 * carries a paginated object `{ data, meta }` (contract v1.1) — the items live
 * one level deeper. Disabled until a brand id resolves so we never fire an
 * unscoped request on boot.
 */
export const useBriefTemplatesQuery = ({
  search = '',
  status = 'active',
  collabType,
  enabled = true,
}: UseBriefTemplatesQueryOptions = {}) => {
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';

  return useQuery<BriefTemplateResponse[], Error>({
    queryKey: briefTemplateKeys.list(brandId, { search, status, collabType }),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<BriefTemplateListPayload>>(
        buildListPath(search, status, collabType)
      );
      return data.data?.data ?? [];
    },
    enabled: enabled && !!brandId,
  });
};
